/***********************
 * SMALL DIAGRAM ACTIVITY
 ************************/

import React, { useEffect, useState, useContext, useRef } from "react"
import PropTypes from "prop-types"
import { withTranslation } from "react-i18next"
import GlobalState from "contexts/GlobalState"
import { Col, Row, CardBody } from "reactstrap"
import { Link } from "react-router-dom"
import ApexCharts from "apexcharts"
import ReactApexChart from "react-apexcharts"

// Locale/weekday related imports
import fi from "apexcharts/dist/locales/fi.json"
import se from "apexcharts/dist/locales/se.json"
import en from "apexcharts/dist/locales/en.json"

const SmallDiagramActivity = props => {
  const [state] = useContext(GlobalState)
  const [language, setLanguage] = useState("fi")
  const [series, setSeries] = useState([])
  const [isLoading, setIsLoading] = useState(true)

  // Muokkaamattomien käyttömäärien ylläpito
  const origMapsRef = useRef({})

  /**
   * Muodostaa kaaviossa käytettävän datan ja tallentaa alkuperäiset arvot refiin.
   *
   * Käyttötarkoitus:
   *  - Skaalaa dataa kaavion visualisointia varten.
   *  - Alkuperäiset mittausarvot talletetaan `origMapsRef`-referenssiin, jotta
   *    niitä voidaan myöhemmin käyttää esim. tooltipissa, päivityksissä tai analyyseissa.
   *
   * @param {Array<[string, number]>} data - Lähdedata (aikaleima, arvo)
   * @param {{ key: string, upTo: number }} opts
   *   - key: tunniste, jolla data tallennetaan `origMapsRef.current[key]`
   *   - upTo: arvo, johon aktiiviset datapisteet (y > 0) skaalataan näyttöä varten
   * @returns {Array<[string, number]>} Kaaviossa piirrettävä data, jossa aktiiviset arvot korvattu upTo-arvolla
   */
  function buildSeries(data, { key, upTo }) {
    const src = (data ?? []).filter(([, y]) => y !== 0)
    origMapsRef.current[key] = new Map(src.map(([x, y]) => [String(x), y]))
    return src.map(([x, y]) => [x, y > 0 ? upTo : y])
  }

  /**
   * Yhdistää peräkkäiset aktiiviset 5 minuutin slotit yhdeksi slotiksi.
   *
   * Käyttötarkoitus:
   *  - Käsittelee aktiivisuusdatan muodossa [ISO-aikaleima, arvo].
   *  - Jos useampi peräkkäinen aikaväli (5 min) on aktiivinen (arvo > 0),
   *    nämä yhdistetään yhdeksi slotiksi.
   *  - Jokaisesta käynnistä palautetaan yksi datapiste, jossa:
   *      x = aikaleima käynnin alusta, keskeltä tai lopusta (parametrin mukaan)
   *      y = aktiivisuusarvojen pyöristetty keskiarvo
   *
   * @param {Array<[string, number]>} data - Aktiivisuusdata 5 min välein
   * @param {"start" | "middle" | "end"} [position="middle"] - Mistä kohtaa käyntiä aikaleima otetaan
   * @returns {Array<[string, number]>} Lista yhdistettyjä datapisteitä muodossa [ISO-aikaleima, keskiarvo]
   */
  const mergeActiveSlotsToOneSlot = (data, position = "middle") => {
    if (!data?.length) return []

    const sorted = [...data].sort((a, b) => new Date(a[0]) - new Date(b[0]))

    const usage = []
    let inUse = false
    let startTs = 0
    let endTs = 0
    let sum = 0
    let count = 0

    for (let i = 0; i < sorted.length; i++) {
      const [iso, val] = sorted[i]
      const ts = new Date(iso).getTime()
      const active = val > 0

      if (active) {
        // Aloitetaan uusi käyttö, jos ei olla jo käytössä
        if (!inUse) {
          inUse = true
          startTs = ts
          sum = 0
          count = 0
        }
        // Kertyvä summa ja laskuri keskiarvoa varten
        sum += val
        count++
        endTs = ts
      }

      // Tarkistetaan, loppuuko aktiivinen käyttöjakso
      const nextActive = i < sorted.length - 1 ? sorted[i + 1][1] > 0 : false
      if (inUse && (!nextActive || i === sorted.length - 1)) {
        // Käyttö päättyy → muodostetaan yksi yhdistetty piste
        let pointTs
        if (position === "start") pointTs = startTs
        else if (position === "end") pointTs = endTs
        else pointTs = (startTs + endTs) / 2 // oletus: keskeltä

        const avg = Math.round(sum / count)
        usage.push([new Date(pointTs).toISOString(), avg])
        inUse = false
      }
    }

    return usage
  }

  // Chart data changed
  useEffect(() => {
    setIsLoading(true)
    if (props.data) {

      let activity = props.data.map(item => [item.date, item.activity])
      let door = props.data.map(item => [item.date, item.door])
      let bed = props.data.map(item => [item.date, item.bed])
      let micro = props.data.map(item => [item.date, item.micro])
      let toilet = props.data.map(item => [item.date, item.toilet])
      let fridge = props.data.map(item => [item.date, item.fridge])
      let coffee = props.data.map(item => [item.date, item.coffee])

      // Bed, Kasvatetaan diagrammissa näytettävää käyttömäärää
      const renderedBedValue = 10
      const bedData = (bed ?? []).map(([x, y]) => [
        x,
        y > 0 ? renderedBedValue : y,
      ])

      // Coffee
      const renderedCoffeeValue = 4
      let coffeeData = (coffee ?? []).map(([x, y]) => [
        x,
        y > 0 ? renderedCoffeeValue : y,
      ])
      let mergedCoffeeData = mergeActiveSlotsToOneSlot(coffeeData, "start")
      let coffeeTimes
      if (coffee.length) {
        // Aja vain, jos dataa on
        const uses = coffee.reduce(
          (count, [, v], i, arr) => {
            // count = kertymä, v = nykyhetken 0/1-arvo
            const prev = i > 0 ? arr[i - 1][1] : 0 // edellinen arvo; alussa oletetaan 0
            // Lisää yksi käyttö, kun alkaa uusi "1-jakso" (nykyinen on 1 ja edellinen ei ollut 1)
            return count + (v === 1 && prev !== 1 ? 1 : 0)
          },
          0 // lähtöarvo laskurille
        )
        coffeeTimes = uses // talleta jaksoittaisten ykkösten (0→1 -siirtymien) lukumäärä
      }

      // WC
      let mergedToiletData = mergeActiveSlotsToOneSlot(toilet, "middle")
      let toiletTimes
      if (toilet.length) {
        let count = 0
        const row = toilet.map(c => {
          count = c[1] > 0 ? count + 1 : 0
          return count
        })
        toiletTimes = row.filter(num => num === 1).length
      }

      // OutsideDoor. upTo = rendreöitävä käyttömäärä diagrammissa
      const renderedDoorValue = 3
      let doorData = buildSeries(door, { key: "door", upTo: renderedDoorValue })
      const doorTimes = door.map(c => c[1]).reduce((a, b) => a + b, 0)

      // Micro
      const renderedWcValue = 2
      let microData = buildSeries(micro, { key: "micro", upTo: renderedWcValue })
      const microTimes = micro.map(c => c[1]).reduce((a, b) => a + b, 0)

      // Fridge
      const renderedFridgeValue = 1
      let fridgeData = buildSeries(fridge, {
        key: "fridge",
        upTo: renderedFridgeValue,
      })
      const fridgeTimes = fridge.map(c => c[1]).reduce((a, b) => a + b, 0)

      // Update usage times to parent
      if (props.useTimesChanged != null) {
        props.useTimesChanged({
          door: doorTimes,
          micro: microTimes,
          fridge: fridgeTimes,
          toilet: toiletTimes,
          coffee: coffeeTimes
        })
      }

      // Set series
      setSeries([
        {
          name: props.t("Bed"),
          value: props.t("Bed"),
          data: bedData,
          type: "area",
        },
        {
          name:
            props.t("Coffee") +
            (coffeeTimes ? ` (${coffeeTimes})` : ""),
          value: props.t("Coffee"),
          data: mergedCoffeeData,
          type: "scatter",
          _key: "coffee",
        },
        {
          name: props.t("All activity"),
          value: props.t("All activity"),
          data: activity,
          type: "area",
        },
        {
          name: props.t("WC") + (toiletTimes ? ` (${toiletTimes})` : ""),
          value: props.t("WC"),
          data: mergedToiletData,
          type: "scatter",
          _key: "wc",
        },
        {
          name:
            props.t("Outside door opened") +
            (doorTimes ? ` (${doorTimes})` : ""),
          value: props.t("Outside door opened"),
          data: doorData,
          type: "scatter",
          _key: "door",
        },
        {
          name:
            props.t("Micro") + (microTimes ? ` (${microTimes})` : ""),
          value: props.t("Micro"),
          data: microData,
          type: "scatter",
          _key: "micro",
        },
        {
          name:
            props.t("Fridge") +
            (fridgeTimes ? ` (${fridgeTimes})` : ""),
          value: props.t("Fridge"),
          data: fridgeData,
          type: "scatter",
          _key: "fridge",
        },
      ])
    }
    setIsLoading(false)
  }, [props.data,])

  // Init language
  useEffect(() => {
    const currentLanguage = localStorage.getItem("i18nextLng")
    if (currentLanguage) {
      if (currentLanguage.startsWith("fi")) {
        setLanguage("fi")
      } else if (currentLanguage.startsWith("sv")) {
        setLanguage("se")
      } else {
        setLanguage("en")
      }
    }
  }, [])

  // Language change
  useEffect(() => {
    switch (state.language) {
      case "fi":
        setLanguage("fi")
        ApexCharts.exec("small-chart", "setLocale", "fi")
        break
      case "en":
        setLanguage("en")
        ApexCharts.exec("small-chart", "setLocale", "en")
        break
      case "sv":
        setLanguage("se")
        ApexCharts.exec("small-chart", "setLocale", "se")
        break
    }
  }, [state.language])

  // Toolbar
  const settingToolbar = t => {
    //const isHome = t === "home"
    return {
      show: true, // isHome
      offsetX: 0,
      offsetY: 0,
      tools: {
        download: true,
        selection: true,
        zoom: true,
        zoomin: true,
        zoomout: true,
        pan: true,
        reset: true,
        customIcons: [],
      },
      export: {
        csv: {
          filename: undefined,
          columnDelimiter: " ",
          headerCategory: "category",
          headerValue: "value",
          dateFormatter(timestamp) {
            return new Date(timestamp).toDateString()
          },
        },
        svg: {
          filename: undefined,
        },
        png: {
          filename: undefined,
        },
      },
      autoSelected: "zoom",
    }
  }

  // Options
  const [options] = useState({
    chart: {
      id: "small-chart",
      locales: [fi, en, se],
      defaultLocale: language,
      toolbar: settingToolbar(props.type),
      stacked: false,
    },
    dataLabels: { enabled: !1 },
    // Bed, Coffee, activity, WC, door, Micro, Fridge
    stroke: { curve: "smooth", width: [2, 0, 2, 0, 0, 0, 0] },

    // plotOptions: {
    //   bar: {
    //     columnWidth: "50%",
    //   },
    // },
    markers: {
      // Bed, Coffee, activity, WC, door, Micro, Fridge
      size: [5, 5, 5, 6, 5, 5, 5],
      colors: props.isDark ? [
        "#a6a6a600", // Bed
        "#833400", // Coffee
        "#48d0f800", // Activity
        "#c47600", // WC
        "#ffffff", // Outside Door
        "#21a156", // Micro
        "#83489b", // Fridge
      ] : [
        "#a6a6a600", // Bed
        "#A04000", // Coffee
        "#48d0f800", // Activity
        "#ff9b00", // WC
        "#ffffff", // Outside Door
        "#2ECC71", // Micro
        "#9B59B6", // Fridge
      ],
      strokeWidth: 1,
      strokeOpacity: props.isDark ? 0.4 : 0.9,
      strokeColors: props.isDark ? [
        "#a6a6a600", // Bed
        "#fff", // Coffee
        "#48d0f800", // Activity
        "#fff", // WC
        "#000000", // Outside Door
        "#fff", // Micro
        "#fff", // Fridge
      ] : [
        "#a6a6a600", // Bed
        "#A04000", // Coffee
        "#48d0f800", // Activity
        "#ff9b00", // WC
        "#000000", // Outside Door
        "#2ECC71", // Micro
        "#9B59B6", // Fridge
      ],
      // https://htmlcolorcodes.com/color-chart/
    },
    xaxis: {
      type: "datetime",
      labels: {
        datetimeUTC: false,
      },
    },
    legend: {
      show: true,
      offsetY: 10,
      markers: {
        offsetX: -2,
      },
      itemMargin: {
        horizontal: 6
      },
    },
    tooltip: {
      followCursor: true,
      shared: false,
      intersect: true,
      x: {
        format: "dd.MM.yyyy HH:mm",
      },
      custom: function (opts) {
        const si = opts.seriesIndex
        const dpi = opts.dataPointIndex
        const desc = opts.w.config.series[si].value
        const key = opts.w.config.series[si]._key
        const point = opts.w.config.series[si].data[dpi]
        const x = Array.isArray(point) ? point[0] : point?.x
        const value = Array.isArray(point) ? point[1] : point?.y

        // hae alkuperäinen arvo oikeasta mapista
        const map = origMapsRef.current[key]
        const original =
          x != null && map?.has(String(x)) ? map.get(String(x)) : value

        if (desc == props.t("Bed")) {
          if (value > 0) {
            return (
              '<div class="py-2 me-2 ms-2 text-muted font-size-12">' +
              "<span>" +
              desc +
              " " +
              props.t("in use") +
              "</span>" +
              "</div>"
            )
          }
          if (value == 0) {
            return (
              '<div class="py-2 me-2 ms-2 text-muted font-size-12">' +
              "<span>" +
              desc +
              " " +
              props.t("not in use") +
              "</span>" +
              "</div>"
            )
          }
        }
        if (desc == props.t("All activity")) {
          return (
            '<div class="py-2 me-2 ms-2 text-muted font-size-12">' +
            "<span>" +
            desc +
            " " +
            value +
            " " +
            "%" +
            "</span>" +
            "</div>"
          )
        }
        if (desc == props.t("WC")) {
          return (
            '<div class="py-2 me-2 ms-2 text-muted font-size-12">' +
            "<span>" +
            props.t("Activity in WC") +
            " " +
            original +
            " " +
            "%" +
            "</span>" +
            "</div>"
          )
        }
        if (desc == props.t("Micro")) {
          return (
            '<div class="py-2 me-2 ms-2 text-muted font-size-12">' +
            "<span>" +
            desc +
            " " +
            props.t("in use") +
            "</span>" +
            "</div>"
          )
        }
        if (desc == props.t("Outside door opened")) {
          if (original == 1) {
            return (
              '<div class="py-2 me-2 ms-2 text-muted font-size-12">' +
              "<span>" +
              desc +
              "</span>" +
              "</div>"
            )
          } else {
            return (
              '<div class="py-2 me-2 ms-2 text-muted font-size-12">' +
              "<span>" +
              desc +
              " " +
              original +
              " " +
              props.t("times") +
              "</span>" +
              "</div>"
            )
          }
        }
        if (desc == props.t("Coffee")) {
          return (
            '<div class="py-2 me-2 ms-2 text-muted font-size-12">' +
            "<span>" +
            desc +
            " " +
            props.t("in use") +
            "</span>" +
            "</div>"
          )
        }
        if (desc == props.t("Fridge")) {
          if (original == 1) {
            return (
              '<div class="py-2 me-2 ms-2 text-muted font-size-12">' +
              "<span>" +
              props.t("Fridge opened") +
              "</span>" +
              "</div>"
            )
          } else {
            return (
              '<div class="py-2 me-2 ms-2 text-muted font-size-12">' +
              "<span>" +
              props.t("Fridge opened") +
              " " +
              original +
              " " +
              props.t("times") +
              "</span>" +
              "</div>"
            )
          }
        }
      },
    },
    //
    colors: props.isDark ? [
      "#a6a6a688", // Bed
      "#833400ff", // Coffee
      "#3294b2", // Activity
      "#c47600", // WC
      "#FFFFFF", // Outside Door
      "#21a156ff", // Micro
      "#83489bff", // Fridge
    ] : [
      "#a6a6a688", // Bed
      "#A04000", // Coffee
      "#48d0f8", // Activity
      "#ff9b00", // WC
      "#FFFFFF", // Outside Door
      "#2ECC71", // Micro
      "#9B59B6", // Fridge
    ],
    fill: {
      // no effect in scatters
      opacity: [1, 1, 1, 1, 1, 1, 1],
      // bed, coffee, activity, wc, outsideDoor, micro, fridge
      type: ["solid", "solid", "gradient", "solid", "solid", "solid", "solid"],
      gradient: {
        type: "vertical",
        shade: props.isDark ? "dark" : "light",
        inverseColors: false,
        opacityFrom: 1,
        opacityTo: props.isDark ? 1 : 0.75,
        stops: [0, 90, 100],
      },
      // solid: {
      //   color: "#ff9b00",
      //   width: 32,
      //   height: 32,
      // },
    },
  })

  return (
    <div>
      {isLoading ? (
        <Row>
          <Col xs="12">
            <CardBody className="text-center" style={{ height: props.size }}>
              <div className="py-5">
                <Link to="#" className="text-primary">
                  <i className="bx bx-loader bx-spin font-size-18 align-middle me-2" />
                  {props.t("Loading")}
                </Link>
              </div>
            </CardBody>
          </Col>
        </Row>
      ) : (
        <ReactApexChart
          options={options}
          series={series}
          type="line"
          height={props.size}
        />
      )}
    </div>
  )
}

SmallDiagramActivity.propTypes = {
  t: PropTypes.any,
  data: PropTypes.any,
  size: PropTypes.number,
  type: PropTypes.string,
  useTimesChanged: PropTypes.func,
  isDark: PropTypes.bool,
}

export default withTranslation()(SmallDiagramActivity)
